import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import logo from "../../assets/images/logo.svg";
import { Form, Formik } from "formik";

import {
  newPasswordSchema,
  resetPasswordSchema,
  verifyCodeSchema,
} from "../../components/common/Validation";
import {
  newPasswordValues,
  resetPasswordValues,
  verifyCodeValues,
} from "../../components/constants/InitialFormValues";
import CustomField from "../../components/common/CustomField";
import CustomLabel from "../../components/common/CustomLabel";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  forgotPassword,
  resetPassword,
  verifyOtp,
} from "../../store/actions/authSlice";
import { showAlert } from "../../store/reducers/alertReducer";

const Steps = {
  RESET_PASSWORD: "RESET_PASSWORD",
  VERIFY_CODE: "VERIFY_CODE",
  NEW_PASSWORD: "NEW_PASSWORD",
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.auth);
  const [step, setStep] = useState(Steps.RESET_PASSWORD);
  const [passwordFields, setPasswordFields] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [cooldown, setCooldown] = useState(30);
  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      setDisabled(true);
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      setDisabled(false);
    }

    return () => clearInterval(timer);
  }, [cooldown]);

  const stepTitles = {
    [Steps.RESET_PASSWORD]: t("Reset Password"),
    [Steps.VERIFY_CODE]: t("Verify Code"),
    [Steps.NEW_PASSWORD]: t("New Password"),
  };

  const handleOnSubmit = (values, { resetForm }) => {
    dispatch(forgotPassword(values))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setPasswordFields((prev) => ({
            ...prev,
            email: values.email,
          }));
          setStep("VERIFY_CODE");
          resetForm();
          setCooldown(30);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleVerifyCode = (values, { resetForm }) => {
    const updatedValues = {
      ...values,
      email: passwordFields.email,
    };

    dispatch(verifyOtp(updatedValues))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setStep("NEW_PASSWORD");
          setPasswordFields((prev) => ({
            ...prev,
            otp: values.otp,
          }));
          resetForm();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };
  const handleNewPassword = (values, { resetForm }) => {
    const updatedValues = {
      ...values,
      email: passwordFields.email,
      // otp: passwordFields.otp,
    };
    // console.log(updatedValues);
    dispatch(resetPassword(updatedValues))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          resetForm();
          navigate("/login");
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const mainTitle = stepTitles[step];

  const handleResendOtp = () => {
    const { email } = passwordFields;
    dispatch(forgotPassword({ email }))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          setCooldown(30); // Reset cooldown
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <div className="login_page">
      <div className="login_form g_center">
        <div className="w-100">
          <img src={logo} width={65} height={72} alt="Ashjar Space Logo" />
          <h1>{t(mainTitle)}</h1>
          {step === "RESET_PASSWORD" && (
            <Formik
              initialValues={resetPasswordValues}
              enableReinitialize={true}
              validationSchema={resetPasswordSchema}
              onSubmit={handleOnSubmit}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="form_field">
                    <CustomLabel htmlFor="email" label={t("Email")} />
                    <CustomField
                      name="email"
                      type="email"
                      placeholder={t("Email your email")}
                      touched={touched}
                      errors={errors}
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary_btn w-100"
                    disabled={isLoading}
                  >
                    {t("Reset")}
                  </button>
                </Form>
              )}
            </Formik>
          )}
          {step === "VERIFY_CODE" && (
            <Formik
              initialValues={verifyCodeValues}
              enableReinitialize={true}
              validationSchema={verifyCodeSchema}
              onSubmit={handleVerifyCode}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="form_field">
                    <CustomLabel htmlFor="otp" label={t("OTP")} />
                    <CustomField
                      name="otp"
                      type="test"
                      placeholder={t("Enter OTP")}
                      touched={touched}
                      errors={errors}
                    />
                  </div>
                  <button
                    className="d-block ms-auto transparent_btn"
                    onClick={handleResendOtp}
                    disabled={disabled || isLoading}
                    type="button"
                  >
                    {disabled
                      ? `${t("Resend OTP")} (${cooldown}s)`
                      : t("Resend OTP")}
                  </button>
                  <button
                    type="submit"
                    className="primary_btn w-100"
                    disabled={isLoading}
                  >
                    {t("Reset")}
                  </button>
                </Form>
              )}
            </Formik>
          )}
          {step === "NEW_PASSWORD" && (
            <Formik
              initialValues={newPasswordValues}
              enableReinitialize={true}
              validationSchema={newPasswordSchema}
              onSubmit={handleNewPassword}
            >
              {({ errors, touched }) => (
                <Form>
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="newPassword"
                      label={t("New Password")}
                    />
                    <CustomField
                      name="newPassword"
                      type="password"
                      placeholder={t("Enter new password")}
                      touched={touched}
                      errors={errors}
                    />
                  </div>
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="confirmPassword"
                      label={t("Confirm password")}
                    />
                    <CustomField
                      name="confirmPassword"
                      type="password"
                      placeholder={t("Confirm password")}
                      touched={touched}
                      errors={errors}
                    />
                  </div>
                  <button
                    type="submit"
                    className="primary_btn w-100"
                    disabled={isLoading}
                  >
                    {t("Reset")}
                  </button>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
